<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { usePortalStore } from '@/store/portal'
import { messageOf } from '@/utils/messages'
import SupportFrame from '@/components/portal/SupportFrame.vue'
import AppIcon from '@/components/portal/AppIcon.vue'
// 나만의 학습노트: 학습창 학습노트 탭에서 수강마다 저장한 메모를 한곳에서 보고 고친다(레거시 studyNote).
const route = useRoute(), auth = useAuthStore(), portal = usePortalStore()
const page = computed(() => portal.pageOf(route.meta.portalPage) || { title: '나만의 학습노트', group: '나의강의실', parent: '/my' })
const notes = ref([]), loading = ref(false), error = ref(''), notice = ref(''), editing = reactive({ id: null, text: '', busy: false, confirm: null })
const time = value => value ? new Date(value).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', dateStyle: 'medium', timeStyle: 'short' }) : ''
let alive = true
onBeforeUnmount(() => { alive = false })
useHead(() => ({ title: `${page.value.title} · TECHEDU` }))
async function load() {
  loading.value = true; error.value = ''
  try { const value = await auth.client.request('/api/user/learning/notes'); if (alive) notes.value = value || [] }
  catch (cause) { if (alive) error.value = messageOf(cause) }
  finally { if (alive) loading.value = false }
}
function edit(item) { editing.id = item.enrollId; editing.text = item.content; editing.confirm = null; notice.value = '' }
async function save(item) {
  if (editing.busy || !editing.text.trim()) return
  editing.busy = true; error.value = ''
  try {
    const value = await auth.client.request(`/api/user/learning/enrollments/${item.enrollId}/note`, { method: 'PUT', body: { content: editing.text } })
    if (!alive) return
    Object.assign(item, { content: value.content, updatedAt: value.updatedAt }); editing.id = null; notice.value = '메모를 저장했습니다.'
  } catch (cause) { if (alive) error.value = messageOf(cause) } finally { if (alive) editing.busy = false }
}
async function remove(item) {
  if (editing.busy) return
  if (editing.confirm !== item.enrollId) { editing.confirm = item.enrollId; return }
  editing.busy = true; error.value = ''
  try { await auth.client.request(`/api/user/learning/enrollments/${item.enrollId}/note`, { method: 'DELETE' }); if (alive) { notes.value = notes.value.filter(row => row !== item); editing.confirm = null; editing.id = null; notice.value = '메모를 삭제했습니다.' } }
  catch (cause) { if (alive) error.value = messageOf(cause) } finally { if (alive) editing.busy = false }
}
onMounted(load)
</script>
<template>
  <SupportFrame :title="page.title" :group="page.group" :parent="page.parent">
    <p class="note-lead">학습창의 <strong>학습노트</strong> 탭에서 저장한 메모를 과정별로 모아 보여 줍니다. 여기서 고치거나 지울 수 있습니다.</p>
    <p v-if="error" class="note-alert error" role="alert">{{ error }}</p>
    <p v-else-if="notice" class="note-alert ok" role="status">{{ notice }}</p>
    <p v-if="loading" class="note-loading" role="status">학습노트를 불러오고 있습니다.</p>
    <p v-else-if="!error && !notes.length" class="note-empty">저장한 학습노트가 없습니다.</p>
    <ul v-else class="note-list">
      <li v-for="item in notes" :key="item.enrollId" class="note-card">
        <header class="note-head">
          <div><h2>{{ item.title }}</h2><p>수강기간 {{ item.studyStartDate }} ~ {{ item.studyEndDate }}<span class="sep" aria-hidden="true">|</span>마지막 저장 {{ time(item.updatedAt) }}</p></div>
          <NuxtLink v-if="item.studying" class="note-go" :to="`/my/learning/${item.enrollId}`"><AppIcon name="book" :size="18" />학습하기</NuxtLink>
        </header>
        <template v-if="editing.id === item.enrollId">
          <textarea v-model="editing.text" class="note-input" maxlength="5000" rows="8" :aria-label="`${item.title} 학습노트`" :disabled="editing.busy" />
          <div class="note-actions"><span class="count">{{ editing.text.length.toLocaleString() }} / 5,000자</span><button type="button" class="btn" :disabled="editing.busy" @click="editing.id = null">취소</button><button type="button" class="btn primary" :disabled="editing.busy || !editing.text.trim()" @click="save(item)">저장</button></div>
        </template>
        <template v-else>
          <p class="note-body">{{ item.content }}</p>
          <div class="note-actions"><button type="button" class="btn" :disabled="editing.busy" @click="edit(item)">수정</button><button type="button" class="btn" :class="{ danger: editing.confirm === item.enrollId }" :disabled="editing.busy" @click="remove(item)">{{ editing.confirm === item.enrollId ? '삭제 확인' : '삭제' }}</button></div>
        </template>
      </li>
    </ul>
  </SupportFrame>
</template>
<style scoped>
.note-lead{margin:0 0 16px;color:#526477;line-height:1.7}
.note-alert{padding:10px 14px;border-radius:6px;font-size:14px}.note-alert.error{background:#fff0ef;color:#a22828}.note-alert.ok{background:#eef8f1;color:#1d6b3a}
.note-loading{padding:40px 0;text-align:center;color:#526477}
.note-empty{height:120px;margin:0 0 10px;border:1px solid #ccc;background:#efefef;color:#666;font-size:15px;font-weight:700;line-height:120px;text-align:center}
.note-list{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:14px}
.note-card{border:1px solid #dde4ea;border-top:2px solid #1b4fd8;background:#fff;padding:18px 20px}
.note-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:10px}.note-head h2{font-size:17px;margin:0 0 4px;color:#1f2a37}.note-head p{margin:0;font-size:13px;color:#6b7785}.sep{margin:0 8px;color:#c2c9d0}
.note-go{display:inline-flex;align-items:center;gap:4px;flex:none;padding:6px 12px;border-radius:4px;background:#1b4fd8;color:#fff;font-size:13px;font-weight:700;text-decoration:none}
.note-body{margin:0;padding:14px;border-radius:6px;background:#f6f8fa;white-space:pre-wrap;line-height:1.75;color:#374151;overflow-wrap:anywhere}
.note-input{display:block;width:100%;padding:12px;border:1px solid #bfc8d1;border-radius:6px;font:inherit;line-height:1.7;resize:vertical;background:#fff}
.note-actions{display:flex;align-items:center;justify-content:flex-end;gap:8px;margin-top:10px}.note-actions .count{margin-right:auto;font-size:13px;color:#6b7785}
.btn{height:34px;padding:0 14px;border:1px solid #c5ced7;border-radius:4px;background:#fff;font-size:13px;font-weight:600;color:#374151;cursor:pointer}.btn:disabled{opacity:.5}.btn.primary{background:#1b4fd8;border-color:#1b4fd8;color:#fff}.btn.danger{border-color:#c73333;color:#c73333}
@media (max-width:640px){.note-head{flex-direction:column}.note-empty{height:auto;padding:40px 10px;line-height:1.5}}
</style>
